import React, { useState, useEffect, useRef } from 'react';
import {
  FileText,
  Edit3,
  Sparkles,
  Brain,
  CheckCircle,
  Zap,
  Target,
  Lightbulb
} from 'lucide-react';
import FeatureStatusBadge from '../shared/FeatureStatusBadge';
import SectionControls from '../shared/SectionControls';

const EssayDevelopmentDemo = ({ data, onAnimationComplete, onContinue, isLastSection = false }) => {
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
          setTimeout(() => setShowAnalysis(true), 1500);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isInView]);

  // Auto-trigger feedback popup demonstration with reduced delay
  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => {
        setPopupPosition({ x: -20, y: 225 }); // Relative to essay container
        setSelectedText('"We\'re moving. Again." My mom said it so casually, like we were switching grocery stores');
        setShowFeedbackPopup(true);
        setTimeout(() => setShowFeedbackPopup(false), 4000); // Reduced from 4000ms
      }, 1500); // Reduced from 2500ms
      return () => clearTimeout(timer);
    }
  }, [isInView]);

  return (
    <div ref={sectionRef} className="max-w-6xl mx-auto px-4">
      {/* Section Header */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 rounded-full">
            <Edit3 className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-medium text-indigo-700">
              Essay Development
            </span>
          </div>
        </div>
        <h2 className="text-4xl font-bold text-slate-900 mb-4">
          Smart Essay Analysis
        </h2>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
        You'll get detailed feedback, breakdowns of patterns and strengths, and specific steps to help you lean in to the lines that hook readers and bring your stories to life. </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left: Smart Revision Interface */}
        <div className="lg:col-span-2 relative">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 bg-indigo-50">
              <h3 className="text-lg font-semibold text-slate-900 mb-1">Smart Revision</h3>
              <p className="text-sm text-slate-600">AI-powered feedback and refinement</p>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="mb-4 p-3 bg-gold-50 rounded-lg border border-azure-200 ml-2">
                <div className="text-sm text-slate-900 mb-2 font-medium">✨ Click highlighted text to see AI feedback</div>
                <div className="text-xs text-slate-600">Get contextual analysis and in-depth feedback on your drafts.</div>
              </div>

              <div className="prose prose-sm max-w-none">
                <p className="leading-relaxed text-slate-800">
                  <span
                    className="bg-gold-100 hover:bg-gold-200 cursor-pointer px-1 rounded transition-colors"
                    onClick={(e) => {
                      setPopupPosition({ x: -20, y: 225 }); // Use same position as auto popup
                      setSelectedText('"We\'re moving. Again." My mom said it so casually, like we were switching grocery stores');
                      setShowFeedbackPopup(true);
                      setTimeout(() => setShowFeedbackPopup(false), 5000);
                    }}
                  >
                    "We're moving. Again." My mom said it so casually, like we were switching grocery stores
                  </span>{" "}
                  instead of uprooting my entire life. I nodded, but inside I felt this heat rising—the kind that comes when you know the easy version of yourself won't survive what's coming next.
                </p>

                <p className="leading-relaxed text-slate-800 mt-4">
                This move dropped me into a school where everyone had grown up together, where friend groups were practically carved into stone. No one was looking for someone new to crowd their lunch table. I wasn’t just trying to make friends; I was trying to matter in a place that had already decided who mattered. That was the part I wasn’t prepared for.                </p>
              </div>

              {/* Feedback Popup - Fixed positioning */}
              {showFeedbackPopup && (
                <div
                  className="absolute bg-white rounded-lg shadow-xl border border-slate-200 p-4 max-w-sm z-50 animate-fadeInScale"
                  style={{
                    left: popupPosition.x,
                    top: popupPosition.y
                  }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-gold-100 text-gold-700 text-xs font-medium rounded">CONTENT</span>
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded">MINOR</span>
                    </div>
                    <button
                      onClick={() => setShowFeedbackPopup(false)}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      ×
                    </button>
                  </div>

                  <div className="text-sm text-slate-600 mb-2 font-medium">SELECTED TEXT:</div>
                  <div className="text-sm italic text-slate-700 mb-3 border-l-2 border-azure-500 pl-3">
                    {selectedText}
                  </div>

                  <div className="text-sm text-slate-600 mb-1 font-medium">FEEDBACK:</div>
                  <div className="text-sm text-slate-800">
                    Excellent opening hook - the casual tone contrasted with life upheaval immediately draws readers into your perspective and establishes the stakes.
                  </div>
                </div>
              )}

              <div className="flex justify-end mt-6">
                <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2">
                  Get More Feedback
                  <Zap className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right: AI Analysis & Tools */}
        <div className="space-y-6">
          {/* Writing Analysis Panel */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-slate-600" />
              <h3 className="font-semibold text-slate-900">Writing Patterns Analysis</h3>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                  <span className="text-sm text-slate-600">Sensory Details</span>
                </div>
                <span className="text-sm text-slate-500">Pending</span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                  <span className="text-sm text-slate-600">Sentence Variety</span>
                </div>
                <span className="text-sm font-medium text-slate-900">8/10</span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                  <span className="text-sm text-slate-600">Content Depth</span>
                </div>
                <span className="text-sm font-medium text-slate-900">10/10</span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                  <span className="text-sm text-slate-600">Structure & Flow</span>
                </div>
                <span className="text-sm font-medium text-slate-900">10/10</span>
              </div>
            </div>
          </div>

          {/* Overall Assessment */}
          {showAnalysis && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
              style={{
                animation: 'fadeInScale 0.6s ease-out forwards'
              }}
            >
              <div className="flex items-center gap-2 mb-4">
                <h3 className="font-semibold text-slate-900">Overall Assessment</h3>
              </div>

              <div className="space-y-4">
                {/* Overall Score */}
                <div className="text-center pb-4 border-b border-slate-200">
                  <div className="text-3xl font-bold text-slate-900 mb-1">85/100</div>
                  <div className="text-sm text-slate-600">Overall Essay Score</div>
                  <div className="text-xs text-teal-600 font-medium mt-1">Strong Foundation</div>
                </div>

                {/* Scoring Breakdown */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-teal-600" />
                      <span className="text-sm text-slate-600">Uniqueness</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full bg-teal-500 transition-all duration-1000" style={{ width: '80%' }}></div>
                      </div>
                      <span className="text-sm font-medium text-slate-900 w-8">8/10</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-azure-600" />
                      <span className="text-sm text-slate-600">Authenticity</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full bg-azure-500 transition-all duration-1000" style={{ width: '85%' }}></div>
                      </div>
                      <span className="text-sm font-medium text-slate-900 w-8">8.5/10</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-magenta-500" />
                      <span className="text-sm text-slate-600">Structure</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full bg-magenta-400 transition-all duration-1000" style={{ width: '70%' }}></div>
                      </div>
                      <span className="text-sm font-medium text-slate-900 w-8">7/10</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-gold-600" />
                      <span className="text-sm text-slate-600">Impact</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full bg-gold-500 transition-all duration-1000" style={{ width: '90%' }}></div>
                      </div>
                      <span className="text-sm font-medium text-slate-900 w-8">9/10</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-azure-50 rounded-lg border border-azure-200">
                <div className="text-sm text-slate-900 mb-2">
                  Your essay demonstrates a captivating perspective and experiences that set you apart from other applicants.
                </div>
                <div className="text-xs text-slate-600 mt-4">
                  <strong>EXAMPLE:</strong> "I treated the new school like a puzzle..."
                </div>
                <div className="text-xs text-slate-600 mt-4">
                  <strong>WHY IT WORKS:</strong> Memorable metaphor for your unique analytical approach.
                </div>
              
              </div>
              <div className="text-xs text-slate-900 p-3 bg-gold-50 mt-2 rounded-lg border border-gold-400">
                  <strong>Next Step:</strong><br></br> Add one more detail that only you could share.
                </div>
            </div>
          )}


        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .animate-fadeInScale {
          animation: fadeInScale 0.3s ease-out forwards;
        }
      `}</style>

      {/* Section Controls */}
      <SectionControls
        onReplay={() => window.location.reload()}
        onContinue={onContinue}
        isLastSection={isLastSection}
        showControls={showAnalysis}
      />
    </div>
  );
};

export default EssayDevelopmentDemo;